// src/pages/PrivacyPolicyPage.js
import React from 'react';
import { Link } from 'react-router-dom';
import './PolicyPage.css';

const LAST_UPDATED = 'March 12, 2026';
const CONTACT_EMAIL = 'piervaconsulting@gmail.com';

function PrivacyPolicyPage() {
  return (
    <div className="policy-page">
      <div className="policy-container">
        <div className="policy-header">
          <Link to="/" className="policy-back">← Back to Login</Link>
          <img src="/favicon.svg" alt="quickLOG" className="policy-logo" />
          <h1>Privacy Policy</h1>
          <p className="policy-meta">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="policy-body">

          <p className="policy-intro">
            PV Business Consulting, LLC ("we", "us", or "our") operates the quickLOG platform
            (the "Service"). This Privacy Policy explains how we collect, use, store, and protect
            information when you or your organization uses quickLOG. By using the Service, you
            agree to the practices described in this policy.
          </p>

          <section>
            <h2>1. Who We Are</h2>
            <p>
              quickLOG is a production intelligence and traceability platform developed and
              operated by <strong>PV Business Consulting, LLC</strong>. We act as a data
              processor on behalf of our business clients (data controllers) and process data
              solely to deliver the Service.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>

            <h3>2.1 Account Information</h3>
            <p>When an account is created for your organization, we collect:</p>
            <ul>
              <li>Usernames (chosen by your administrator)</li>
              <li>Passwords (stored as one-way cryptographic hashes — we never store plaintext passwords)</li>
              <li>User roles and permission assignments</li>
              <li>Two-factor authentication secrets (stored encrypted, if MFA is enabled)</li>
            </ul>

            <h3>2.2 Business & Operational Data</h3>
            <p>Data entered by your organization's users in the course of using the platform:</p>
            <ul>
              <li>Production logs (batch records, quantities, dates, operators)</li>
              <li>Ingredient and product information (names, recipes, quantities)</li>
              <li>Inventory adjustments and stock levels</li>
              <li>Traceability records linking raw materials to finished goods</li>
              <li>Customer and order information (if applicable modules are enabled)</li>
              <li>Quality assurance and CCP records</li>
              <li>Environmental and labor data (if analytics modules are enabled)</li>
            </ul>

            <h3>2.3 Technical & Usage Data</h3>
            <ul>
              <li>Browser session tokens (stored in browser localStorage, not cookies)</li>
              <li>IP addresses and browser type for security and audit purposes</li>
              <li>API request logs (retained for a limited period for debugging)</li>
            </ul>

            <h3>2.4 Company Branding</h3>
            <p>
              Company logo images uploaded through the platform are stored in Amazon S3
              and served via a secure URL.
            </p>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the information collected exclusively to:</p>
            <ul>
              <li>Provide, maintain, and improve the quickLOG platform</li>
              <li>Authenticate users and enforce access controls</li>
              <li>Enable production traceability, inventory management, and analytics features</li>
              <li>Respond to support requests</li>
              <li>Ensure security and prevent unauthorized access</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p>
              We do <strong>not</strong> sell, rent, or share your data with third parties
              for marketing purposes.
            </p>
          </section>

          <section>
            <h2>4. Data Storage & Security</h2>
            <p>Your data is stored on the following infrastructure:</p>
            <ul>
              <li><strong>Database:</strong> MySQL hosted on JawsDB (Heroku add-on), located in the United States</li>
              <li><strong>Application server:</strong> Heroku (Salesforce), United States</li>
              <li><strong>File storage:</strong> Amazon S3 (AWS), United States</li>
              <li><strong>Frontend hosting:</strong> Netlify, United States</li>
            </ul>
            <p>
              We implement the following security measures:
            </p>
            <ul>
              <li>All data in transit is encrypted using TLS/HTTPS</li>
              <li>Passwords are hashed using bcrypt (industry-standard one-way hashing)</li>
              <li>Optional two-factor authentication (TOTP) for user accounts</li>
              <li>JWT-based session tokens with expiration</li>
              <li>Role-based access control (RBAC) limiting data access by user role</li>
              <li>Multi-tenant data isolation: each company's data is logically separated</li>
            </ul>
          </section>

          <section>
            <h2>5. Data Retention</h2>
            <p>
              We retain your organization's data for as long as your account is active or as
              needed to provide the Service. Upon termination of your subscription:
            </p>
            <ul>
              <li>You may request an export of your data before account closure</li>
              <li>Account and operational data will be deleted within 90 days of termination</li>
              <li>Backups may persist for up to an additional 30 days before full purge</li>
            </ul>
          </section>

          <section>
            <h2>6. Your Rights</h2>
            <p>
              Depending on your location, you or your organization's users may have the right to:
            </p>
            <ul>
              <li><strong>Access</strong> the personal data we hold about you</li>
              <li><strong>Correct</strong> inaccurate or incomplete data</li>
              <li><strong>Delete</strong> your account and associated personal data</li>
              <li><strong>Restrict</strong> or object to certain processing</li>
              <li><strong>Data portability</strong> — receive a copy of your data in a machine-readable format</li>
            </ul>
            <p>
              Requests relating to personal data should be submitted by your organization's
              administrator to us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
              We will respond within 30 days.
            </p>
          </section>

          <section>
            <h2>7. Cookies & Local Storage</h2>
            <p>
              quickLOG does not use advertising or tracking cookies. The platform uses
              browser <code>localStorage</code> solely to store your authentication token
              (JWT) for session management. This data is cleared when you log out or when
              the token expires.
            </p>
          </section>

          <section>
            <h2>8. Third-Party Services</h2>
            <p>The platform relies on the following third-party infrastructure providers:</p>
            <ul>
              <li><strong>Heroku (Salesforce)</strong> — application hosting</li>
              <li><strong>JawsDB</strong> — managed MySQL database</li>
              <li><strong>Amazon Web Services (AWS S3)</strong> — file storage</li>
              <li><strong>Netlify</strong> — frontend hosting</li>
            </ul>
            <p>
              Each provider operates under their own privacy and security policies. We select
              providers that maintain appropriate data protection standards.
            </p>
          </section>

          <section>
            <h2>9. International Data Transfers</h2>
            <p>
              Data is stored and processed in the United States. If your organization is located
              outside the US (including within the European Economic Area), data transferred to
              us is subject to US data protection laws. For EEA clients requiring a Data
              Processing Agreement (DPA) under GDPR Article 28, please contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </p>
          </section>

          <section>
            <h2>10. Children's Privacy</h2>
            <p>
              quickLOG is a business-to-business platform not directed at individuals under
              the age of 18. We do not knowingly collect personal information from minors.
            </p>
          </section>

          <section>
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify account
              administrators of material changes via the registered contact email at least
              14 days before changes take effect. Continued use of the Service after the
              effective date constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2>12. Contact</h2>
            <p>
              For privacy-related questions or requests, contact us at:<br />
              <strong>PV Business Consulting, LLC</strong><br />
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </p>
          </section>

        </div>

        <div className="policy-footer">
          <p>&copy; {new Date().getFullYear()} quickLOG by PV Business Consulting, LLC. All rights reserved.</p>
          <div className="policy-footer-links">
            <Link to="/terms">Terms of Service</Link>
            <Link to="/">Back to Login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicyPage;
